/**
 * Duva — Shared TypeScript types used by both web and worker packages.
 * Import from "@duva/shared" in either app.
 */

export type UserRole = 'admin' | 'manager' | 'employee'
export type NudgeChannel = 'slack' | 'teams' | 'email'
export type NudgeFrequency = 'daily' | 'weekly' | 'off'
export type OrgPlan = 'free' | 'enterprise'
export const NUDGE_CATEGORIES = [
  'leadership',
  'inclusion',
  'burnout',
  'safety',
  'recognition',
  'goals',
  'first time manager',
] as const

export type NudgeCategory = (typeof NUDGE_CATEGORIES)[number]

export interface CategoryMetadata {
  bg: string
  text: string
  border: string
  /** Tailwind classes for components using CSS-in-JS or standard Tailwind */
  tailwind: string
}

export const CATEGORY_METADATA: Record<NudgeCategory, CategoryMetadata> = {
  leadership: {
    bg: '#EBF6FB',
    text: '#1a3a40',
    border: '#a7d8e0',
    tailwind: 'bg-[#EBF6FB] text-ink border-[#a7d8e0]'
  },
  inclusion: {
    bg: '#eef5ec',
    text: '#1a3a40',
    border: '#B8C7B1',
    tailwind: 'bg-[#eef5ec] text-ink border-[#B8C7B1]'
  },
  burnout: {
    bg: '#fdf2f2',
    text: '#1a3a40',
    border: '#e8cfcf',
    tailwind: 'bg-[#fdf2f2] text-ink border-[#e8cfcf]'
  },
  safety: {
    bg: '#EDE8F5',
    text: '#1a3a40',
    border: '#d4cce8',
    tailwind: 'bg-[#EDE8F5] text-ink border-[#d4cce8]'
  },
  recognition: {
    bg: '#fdf8f0',
    text: '#1a3a40',
    border: '#e8d4b8',
    tailwind: 'bg-[#fdf8f0] text-ink border-[#e8d4b8]'
  },
  goals: {
    bg: '#E8F0EE',
    text: '#1a3a40',
    border: '#B8C7B1',
    tailwind: 'bg-[#E8F0EE] text-ink border-brand-sage'
  },
  'first time manager': {
    bg: '#eefcfc',
    text: '#2a8a8a',
    border: '#a7d8e0',
    tailwind: 'bg-[#eefcfc] text-[#2a8a8a] border-[#a7d8e0]'
  }
}

export type SystemTarget = 'system1' | 'system2'
export type DeliveryOutcome = 'completed' | 'skipped' | 'snoozed'

export interface Organization {
  id: string
  name: string
  slack_workspace_id: string | null
  teams_tenant_id: string | null
  plan: OrgPlan
  employee_count: number
  created_at: string
}

export interface User {
  id: string
  org_id: string
  email: string
  name: string
  role: UserRole
  slack_user_id: string | null
  teams_user_id: string | null
  preferred_channel: NudgeChannel
  nudge_frequency: NudgeFrequency
  created_at: string
}

export type NudgeTemplateStatus = 'draft' | 'pending_review' | 'approved' | 'rejected'

export interface NudgeTemplate {
  id: string
  category: NudgeCategory
  title: string
  body: string
  channel: NudgeChannel | 'any'
  system_target: SystemTarget
  science_source: string | null
  effectiveness_score: number
  status?: NudgeTemplateStatus
  source_refs?: string | null
  review_notes?: string | null
  reviewed_at?: string | null
}

export interface NudgeDelivery {
  id: string
  org_id: string
  user_id: string
  nudge_id: string
  channel: NudgeChannel
  sent_at: string
  opened_at: string | null
  action_taken_at: string | null
  outcome: DeliveryOutcome | null
}

export interface EngagementScore {
  id: string
  org_id: string
  team_id: string | null
  score: number
  risk_flags: string[]
  computed_at: string
}

export interface AccountabilityPair {
  id: string
  org_id: string
  user_a_id: string
  user_b_id: string
  track: string
  active: boolean
  created_at: string
}

// ── API shapes ────────────────────────────────────────────────────────────────

export interface SendNudgeRequest {
  userIds: string[]
  nudgeId?: string
  channel?: NudgeChannel
  idempotencyKey: string
}

export interface UpdateDeliveryRequest {
  outcome: DeliveryOutcome
  openedAt?: string
  actionTakenAt?: string
}

export interface UpdateDeliveryResponse {
  status: string
  isFirstNudge?: boolean
}

export interface AnalyticsSummary {
  engagementScore: Pick<EngagementScore, 'score' | 'risk_flags' | 'computed_at'> | null
  weeklyStats: { sent: number; opened: number; acted: number } | null
  activeMemberCount: number
}

export interface NudgeStat {
  id: string
  title: string
  category: NudgeCategory
  total_sent: number
  total_opened: number
  total_acted: number
  open_rate: number
  action_rate: number
}

// ── Engagement risk flags (codes from engagementScorer + sentiment) ───────────
/** Stakeholder- and manager-facing copy; used on Home and shareable HTML reports. */
export interface RiskFlagCopy {
  title: string
  detail: string
}

const RISK_FLAG_COPY: Record<string, RiskFlagCopy> = {
  low_action_rate: {
    title: 'Low recent action rate',
    detail:
      'A small share of delivered nudges had an action recorded in the last 7 days. Worth checking relevance, timing, or team capacity.',
  },
  low_open_rate: {
    title: 'Low open rate on nudges',
    detail:
      'Many recent nudges were not opened. Channel noise, timing, or how prompts appear may deserve a look.',
  },
  low_participation: {
    title: 'Limited active participation',
    detail:
      'Relatively few people with nudges on received a nudge in the last 7 days. Invites, channels, or opt-outs may explain it.',
  },
  no_streaks_detected: {
    title: 'Few multi-day action streaks yet',
    detail:
      'Very few people acted on nudges across several consecutive recent days. Common when the program is new or volume is light; habits often strengthen over time.',
  },
  ai_at_risk: {
    title: 'Pattern suggests elevated concern',
    detail:
      'Aggregated signals from recent team activity matched elevated stress or disengagement patterns. Use alongside other context—not a diagnosis of individuals.',
  },
  ai_concerning: {
    title: 'Pattern worth a closer look',
    detail:
      'Aggregated sentiment from recent activity flagged something managers may want to review with appropriate discretion.',
  },
}

/** Resolve internal flag code to display title and explanation. Unknown codes get title-cased slug, empty detail. */
export function getRiskFlagCopy(code: string): RiskFlagCopy {
  const known = RISK_FLAG_COPY[code]
  if (known) return known
  const title = code
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase())
  return { title, detail: '' }
}
